// External Dependencies
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Field, FormikProvider, useFormik } from 'formik'
import * as Yup from 'yup'

// Icons
import { IoArrowBackOutline } from 'react-icons/io5'

// Custom Hooks
import useGoBack from '../../hooks/useGoBack'
import useUserStore from '../../utils/state/useUserStore'

// API Services
import axiosInterceptorInstance from '../../utils/axios/axiosInterceptorInstance'

// Components
import Input from '../../components/common/CustomInput'
import CustomToast from '../../components/Custom/CustomToast'
import PageHeader from '../../components/Custom/PageHeader/PageHeader'

const UserAdd = () => {
  const navigate = useNavigate()

  // Hooks
  const goBack = useGoBack()
  const userPermissions = useUserStore((state) => state.user) || []

  // State
  const [roleList, setRoleList] = useState([])
  const [error, setError] = useState('')

  // Validation Schema
  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email('Please enter a valid email address')
      .required('Email address is required'),
    password: Yup.string()
      .min(6, 'Password must contain at least 6 characters')
      .required('Password is required')
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        'Password must contain at least one uppercase letter, one lowercase letter, and one number',
      ),
    confirm_password: Yup.string()
      .oneOf([Yup.ref('password'), null], 'Passwords do not match')
      .required('Please confirm your password'),
    role: Yup.string().required('Please select a role'),
  })

  // Fetch roles on component mount
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await axiosInterceptorInstance.get('/roles')
        if (response.data.roles.length > 0) {
          const selectOptions = response.data.roles.map((role) => ({
            label: role.name,
            value: role._id,
          }))
          setRoleList(selectOptions)
        }
      } catch (err) {
        CustomToast({
          message: 'Failed to fetch roles. Please refresh the page.',
          type: 'error',
        })
      }
    }
    fetchRoles()
  }, [])

  // Form handling with Formik
  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      confirm_password: '',
      role: '',
    },
    validationSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      if (!userPermissions.includes('user:create')) {
        CustomToast({
          message: 'You do not have permission to create users',
          type: 'error',
        })
        return
      }

      try {
        await axiosInterceptorInstance.post('/users', values)
        CustomToast({
          message: 'User created successfully',
          type: 'success',
        })
        resetForm()
        setError('')
        navigate('/user')
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || 'Failed to create user'
        if (error.response?.status === 409) {
          setError('This email address is already registered')
        } else {
          CustomToast({
            message: errorMessage,
            type: 'error',
          })
        }
      } finally {
        setSubmitting(false)
      }
    },
  })

  return (
    <div>
      <PageHeader>
        <div className='flex gap-2 items-center justify-between w-full'>
          <div className='flex gap-2 items-center font-medium'>
            <button
              onClick={goBack}
              className='h-9 w-9 flex justify-center items-center rounded-full text-xl cursor-pointer transition-all duration-300 hover:text-white hover:bg-prime'
              aria-label='Go back'
            >
              <IoArrowBackOutline />
            </button>
            <h1 className='text-2xl'>Add User</h1>
          </div>
        </div>
      </PageHeader>

      <FormikProvider value={formik}>
        <form
          className='w-full sm:w-[500px] mx-auto'
          onSubmit={formik.handleSubmit}
          noValidate
        >
          {/* Email Field */}
          <div className='mb-4'>
            <Field
              name='email'
              type='email'
              label='Email Address'
              placeholder='Enter email address'
              component={Input}
              autoComplete='email'
            />
          </div>

          {/* Password Field */}
          <div className='mb-4'>
            <Field
              name='password'
              type='password'
              label='Password'
              placeholder='Enter password'
              component={Input}
              autoComplete='new-password'
            />
          </div>

          {/* Confirm Password Field */}
          <div className='mb-4'>
            <Field
              name='confirm_password'
              type='password'
              label='Confirm Password'
              placeholder='Confirm your password'
              component={Input}
              autoComplete='new-password'
            />
          </div>

          {/* Role Selection */}
          <div className='my-6'>
            <label className='block font-medium mb-2' htmlFor='role'>
              Select Role:
            </label>
            <select
              id='role'
              className='w-full border-[2px] border-light-gray focus:border-prime rounded-lg p-3 focus:outline-none focus:border-orange invalid:border-red-700'
              name='role'
              value={formik.values.role}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              <option value='' disabled>
                Select a Role
              </option>
              {roleList.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
            {formik.touched.role && formik.errors.role && (
              <div className='text-red-700 text-sm mt-1'>
                {formik.errors.role}
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className='text-red-700 text-sm my-4 font-normal' role='alert'>
              {error}
            </div>
          )}

          {/* Submit Button */}
          <div className='mt-4'>
            <button
              type='submit'
              disabled={formik.isSubmitting}
              className='w-full py-3 rounded-md sm:w-[500px] text-prime bg-white border border-prime hover:bg-prime hover:text-white transition-all duration-300 disabled:bg-prime disabled:pointer-events-none disabled:cursor-auto disabled:bg-opacity-80 disabled:text-white'
            >
              {formik.isSubmitting ? 'Creating User...' : 'Add User'}
            </button>
          </div>
        </form>
      </FormikProvider>
    </div>
  )
}

export default UserAdd
